/**
 * 子账号服务模块：处理子账号管理相关的 API 请求
 */

import { apiService } from './api'
import type {
  SubAccountListResponse,
  SubAccountListParams,
  CreateSubAccountRequest,
  CreateSubAccountResponse,
  DeleteSubAccountResponse,
  UpdateSubAccountStatusRequest,
  UpdateSubAccountStatusResponse,
} from '@/types/subAccount'

/**
 * API 响应结构
 */
interface ApiResponseWrapper<T> {
  code: number
  message: string
  data: T
}

const SUB_ACCOUNT_ENDPOINT = '/api/user/sub-accounts' as const

/**
 * 获取子账号列表
 * @param params 查询参数
 * @returns 子账号列表响应
 */
export async function getSubAccountList(
  params?: SubAccountListParams
): Promise<SubAccountListResponse> {
  const queryParams: Record<string, string> = {}

  if (params?.page !== undefined) {
    queryParams['page'] = String(params.page)
  }
  if (params?.pageSize !== undefined) {
    queryParams['pageSize'] = String(params.pageSize)
  }
  if (params?.keyword !== undefined && params.keyword.length > 0) {
    queryParams['keyword'] = params.keyword
  }
  if (params?.status !== undefined) {
    queryParams['status'] = params.status
  }

  const response = await apiService.get<ApiResponseWrapper<SubAccountListResponse>>(
    SUB_ACCOUNT_ENDPOINT,
    Object.keys(queryParams).length > 0 ? queryParams : undefined
  )
  return response.data
}

/**
 * 创建子账号
 * @param data 创建子账号请求参数
 * @returns 创建子账号响应
 */
export async function createSubAccount(
  data: CreateSubAccountRequest
): Promise<CreateSubAccountResponse> {
  const response = await apiService.post<ApiResponseWrapper<CreateSubAccountResponse>>(
    SUB_ACCOUNT_ENDPOINT,
    data
  )
  return response.data
}

/**
 * 删除子账号
 * @param id 子账号 ID
 * @returns 删除响应
 */
export async function deleteSubAccount(
  id: string
): Promise<DeleteSubAccountResponse> {
  const response = await apiService.delete<ApiResponseWrapper<DeleteSubAccountResponse>>(
    `${SUB_ACCOUNT_ENDPOINT}/${id}`
  )
  return response.data
}

/**
 * 更新子账号状态
 * @param id 子账号 ID
 * @param data 更新状态请求参数
 * @returns 更新状态响应
 */
export async function updateSubAccountStatus(
  id: string,
  data: UpdateSubAccountStatusRequest
): Promise<UpdateSubAccountStatusResponse> {
  const response = await apiService.put<ApiResponseWrapper<UpdateSubAccountStatusResponse>>(
    `${SUB_ACCOUNT_ENDPOINT}/${id}/status`,
    data
  )
  return response.data
}
